import { useState, useEffect, useCallback } from 'react';
import { useSearchTokensQuery } from '../redux/api/public/tokens';
import { PublicKey } from '@solana/web3.js';
import { useSelector } from 'react-redux';
import { selectTokensWithUSDBalance } from '../redux/slices/tokenSlice';

export const useTokenSearch = (customBaseTokens = null, filterToken = null) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [pageNo, setPageNo] = useState(0);
  const [allTokens, setAllTokens] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const tokensWithBalance = useSelector((state) =>
    selectTokensWithUSDBalance(state),
  );

  // Use custom base tokens if provided, otherwise use tokens with balance
  const baseTokens = customBaseTokens || tokensWithBalance;

  // If custom tokens are provided, we should only search within them (no API calls)
  const shouldUseOnlyCustomTokens = customBaseTokens !== null;

  // Use Solana's built-in validation to check if search term is a valid address
  const isAddressSearch = useCallback((term) => {
    if (!term || term.length < 32 || term.length > 44) return false;

    try {
      new PublicKey(term);
      return true; // If no error was thrown, it's a valid address
    } catch {
      return false; // PublicKey constructor failed, not a valid address
    }
  }, []);

  const {
    data: searchData,
    isLoading,
    isFetching,
    error,
  } = useSearchTokensQuery(
    {
      name: !isAddressSearch(debouncedSearchTerm)
        ? debouncedSearchTerm || undefined
        : undefined,
      addresses: isAddressSearch(debouncedSearchTerm)
        ? [debouncedSearchTerm]
        : undefined,
      category: selectedCategory !== 'ALL' ? selectedCategory : undefined,
      pageNo,
      pageAmount: 50,
    },
    {
      skip: shouldUseOnlyCustomTokens, // Skip API calls when using only custom tokens
    },
  );

  // Debounce the search term for API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Handle local filtering when using custom tokens only
  useEffect(() => {
    if (shouldUseOnlyCustomTokens) {
      // Filter baseTokens locally based on search term
      let filteredTokens = baseTokens;

      // Filter out the excluded token if provided
      if (filterToken) {
        filteredTokens = filteredTokens.filter(
          (token) => token.address !== filterToken.address,
        );
      }

      if (searchTerm) {
        const lowerSearchTerm = searchTerm.toLowerCase();
        filteredTokens = filteredTokens.filter(
          (token) =>
            token.name?.toLowerCase().includes(lowerSearchTerm) ||
            token.symbol?.toLowerCase().includes(lowerSearchTerm) ||
            token.address?.toLowerCase().includes(lowerSearchTerm),
        );
      }

      setAllTokens(filteredTokens);
      setHasMore(false); // No pagination for local filtering
      return;
    }
  }, [shouldUseOnlyCustomTokens, baseTokens, searchTerm, filterToken]);

  // Process backend search results
  useEffect(() => {
    if (!shouldUseOnlyCustomTokens && searchData?.tokens) {
      if (pageNo === 0) {
        // First page, replace all tokens
        if (searchTerm || selectedCategory !== 'ALL') {
          const searchResultAddresses = new Set(
            searchData?.tokens.map((token) => token.address),
          );
          let baseTokensAndInSearchResult = baseTokens.filter((token) =>
            searchResultAddresses.has(token.address),
          );
          let newTokens = searchData.tokens.filter(
            (token) =>
              !baseTokensAndInSearchResult
                .map((token) => token.address)
                .includes(token.address),
          );

          // Filter out the excluded token if provided
          if (filterToken) {
            baseTokensAndInSearchResult = baseTokensAndInSearchResult.filter(
              (token) => token.address !== filterToken.address,
            );
            newTokens = newTokens.filter(
              (token) => token.address !== filterToken.address,
            );
          }

          const result = [...baseTokensAndInSearchResult, ...newTokens];
          setAllTokens(result);
        } else {
          const existingAddresses = new Set(
            baseTokens.map((token) => token.address),
          );
          let newTokens = searchData.tokens.filter(
            (token) => !existingAddresses.has(token.address),
          );
          let filteredBaseTokens = baseTokens;

          // Filter out the excluded token if provided
          if (filterToken) {
            filteredBaseTokens = baseTokens.filter(
              (token) => token.address !== filterToken.address,
            );
            newTokens = newTokens.filter(
              (token) => token.address !== filterToken.address,
            );
          }

          const result = [...filteredBaseTokens, ...newTokens];
          setAllTokens(result);
        }
      } else {
        // Subsequent pages, append tokens and remove duplicates
        setAllTokens((prev) => {
          const existingAddresses = new Set(prev.map((token) => token.address));
          let newTokens = searchData.tokens.filter(
            (token) => !existingAddresses.has(token.address),
          );

          // Filter out the excluded token if provided
          if (filterToken) {
            newTokens = newTokens.filter(
              (token) => token.address !== filterToken.address,
            );
          }

          const result = [...prev, ...newTokens];
          return result;
        });
      }

      // Check if there are more pages
      const totalCount = searchData.count || 0;
      const currentCount = (pageNo + 1) * 50;
      setHasMore(currentCount < totalCount);
    }
  }, [
    shouldUseOnlyCustomTokens,
    searchData,
    pageNo,
    baseTokens,
    searchTerm,
    selectedCategory,
    filterToken,
  ]);

  // Reset pagination when search or category changes
  useEffect(() => {
    setPageNo(0);
  }, [searchTerm, selectedCategory]);

  // Load more tokens (for infinite scroll)
  const loadMore = useCallback(() => {
    if (hasMore && !isFetching) {
      setPageNo((prev) => prev + 1);
    }
  }, [hasMore, isFetching]);

  return {
    // State
    searchTerm,
    selectedCategory,
    displayTokens: allTokens,
    isLoading: shouldUseOnlyCustomTokens ? false : isLoading, // No loading state for local filtering
    isFetching: shouldUseOnlyCustomTokens ? false : isFetching, // No fetching state for local filtering
    hasMore,
    error,

    // Actions
    setSearchTerm,
    setSelectedCategory,
    loadMore,

    // Computed
    totalCount: shouldUseOnlyCustomTokens
      ? allTokens.length
      : searchData?.count || 0,
  };
};
